import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Dialog, Menu } from "react-aria-components";
import {
  MoreVertical,
  Pencil,
  Plus,
  Copy,
  ArrowUpToLine,
  Star,
  FolderInput,
  Archive,
  Settings2,
  Trash2,
} from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export interface FacilityKebabMenuProps {
  node: FacilityNode;
  isFavorite: boolean;
  /** Node-level manage (manage/access_manage or bypass) — gates Rename / Add child / Manage facility. */
  canManage: boolean;
  /** Subtree-wide manage (mirror of the server's requireManageSubtree) — gates Archive AND Delete (both need manage on
   *  the whole subtree; the client gate is best-effort, the server is authoritative). */
  canArchive: boolean;
  /** The caller can Duplicate (subtree manage AND create-authz on the parent; root = bypass-only) — WIDER than Archive/
   *  Delete because Duplicate also creates a sibling (ADR-008); a distinct gate, not `canArchive`. */
  canDuplicate: boolean;
  /** The caller can Insert above (create a SIBLING = a child of this node's parent; ADR-009). Parent-derived create
   *  authz + active-parent; disabled on roots. Computed by the parent from the CANONICAL tree. */
  canInsertAbove: boolean;
  /** The caller can move this node (ADR-005 source-side authz mirror) — gates "Move to…". */
  movable: boolean;
  /** Roving-tabindex: only the active row's controls are Tab-reachable. */
  tabbable: boolean;
  /** Offer "Insert above". Default true (classic tree); the drill-down passes false (owner request 2026-10-21). */
  showInsertAbove?: boolean;
  onRename: () => void;
  onAddChild: () => void;
  onManage: () => void;
  onMoveTo: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onInsertAbove: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
}

/**
 * The per-card actions menu (⋮). Built on a **Popover + Dialog** — NOT a bare `role="menu"` — because the panel leads
 * with a STATIC facility-name header: screen readers in application mode navigate menuitems only and would skip static
 * text inside a menu (see `AccountPanel` + arc-conventions §3). So the name is ordinary text in the Dialog body and the
 * ACTIONS are a separate `<Menu>` below it.
 *
 * Wires the built actions (Rename · Add child · Insert above · Duplicate · Move to… · Archive · Delete · Manage
 * facility · favorites toggle). **Insert above** creates a SIBLING (a child of this node's parent, ADR-009) — grouped
 * with Add child; its own `canInsertAbove` gate (parent-derived create authz + active-parent; disabled on roots).
 * Actions are keyed by id; `onAction` closes the popover first, then runs the handler. Archive, Delete AND Duplicate
 * are subtree-gated (`canArchive` — all need manage on the whole subtree); destructive-Delete opens a type-to-confirm
 * dialog; shown only in the normal view (the Archived tab renders a dedicated Unarchive button, not this kebab).
 */
export function FacilityKebabMenu({
  node,
  isFavorite,
  canManage,
  canArchive,
  canDuplicate,
  canInsertAbove,
  movable,
  tabbable,
  showInsertAbove = true,
  onRename,
  onAddChild,
  onManage,
  onMoveTo,
  onArchive,
  onDuplicate,
  onInsertAbove,
  onDelete,
  onToggleFavorite,
}: FacilityKebabMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const actions: Record<string, () => void> = {
    rename: onRename,
    "add-child": onAddChild,
    "insert-above": onInsertAbove,
    favorite: onToggleFavorite,
    move: onMoveTo,
    archive: onArchive,
    duplicate: onDuplicate,
    delete: onDelete,
    manage: onManage,
  };

  const disabledKeys = new Set<string>();
  if (!canManage) {
    disabledKeys.add("rename");
    disabledKeys.add("add-child");
    disabledKeys.add("manage");
  }
  if (!movable) disabledKeys.add("move");
  if (!canArchive) {
    disabledKeys.add("archive");
    disabledKeys.add("delete"); // delete needs manage on the whole subtree, same as archive
  }
  // Duplicate needs subtree manage AND create-authz on the parent (a root is bypass-only) — a WIDER gate than Archive/
  // Delete (it creates a sibling), computed by the parent (ADR-008; panel).
  if (!canDuplicate) disabledKeys.add("duplicate");
  // Insert above = create a SIBLING (a child of this node's parent) — needs create-authz on the (active) parent, so it's
  // disabled on roots + when the parent is archived/unmanageable, computed by the parent (ADR-009; panel).
  if (!canInsertAbove) disabledKeys.add("insert-above");

  return (
    <PopoverTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="icon"
        excludeFromTabOrder={!tabbable}
        aria-label={t("facilityManagement.actions.kebab", { name: node.name })}
      >
        <MoreVertical className="text-muted-foreground size-4" />
      </Button>

      {/* Open to the RIGHT (into the manuals pane) so the menu never overlays the facility tree; react-aria flips it
          automatically if there isn't room on that side. */}
      <Popover className="w-56 gap-0 p-0" placement="right top">
        <Dialog aria-label={t("facilityManagement.actions.kebab", { name: node.name })} className="outline-none">
          {/* Static identity header — ordinary text, deliberately OUTSIDE the role="menu" (a11y: SRs skip static
              text inside a menu). */}
          <div className="border-border truncate border-b px-2.5 py-2 text-sm font-medium">{node.name}</div>

          <Menu
            aria-label={t("facilityManagement.actions.kebabAlt", { name: node.name })}
            className="flex flex-col p-1 outline-none"
            disabledKeys={disabledKeys}
            onAction={(key) => {
              setOpen(false);
              actions[String(key)]?.();
            }}
          >
            <DropdownMenuItem id="rename" textValue={t("facilityManagement.actions.rename")}>
              <Pencil />
              {t("facilityManagement.actions.rename")}
            </DropdownMenuItem>
            <DropdownMenuItem id="add-child" textValue={t("facilityManagement.actions.addChild")}>
              <Plus />
              {t("facilityManagement.actions.addChild")}
            </DropdownMenuItem>
            {/* Insert above = add a SIBLING (a child of this node's parent) — grouped with Add child (both create).
                Hidden in the drill-down (owner request 2026-10-21); the classic tree keeps it. */}
            {showInsertAbove ? (
              <DropdownMenuItem id="insert-above" textValue={t("facilityManagement.actions.insertAbove")}>
                <ArrowUpToLine />
                {t("facilityManagement.actions.insertAbove")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem id="duplicate" textValue={t("other.duplicate")}>
              <Copy />
              {t("other.duplicate")}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem id="favorite" textValue={isFavorite ? t("facilityManagement.actions.removeFromFavourites") : t("facilityManagement.actions.addToFavourites")}>
              <Star />
              {isFavorite ? t("facilityManagement.actions.removeFromFavourites") : t("facilityManagement.actions.addToFavourites")}
            </DropdownMenuItem>
            <DropdownMenuItem id="move" textValue={t("facilityManagement.actions.moveTo")}>
              <FolderInput />
              {t("facilityManagement.actions.moveTo")}
            </DropdownMenuItem>
            <DropdownMenuItem id="archive" textValue={t("facilityManagement.actions.archive")}>
              <Archive />
              {t("facilityManagement.actions.archive")}
            </DropdownMenuItem>
            <DropdownMenuItem id="manage" textValue={t("facilityManagement.actions.manageFacility")}>
              <Settings2 />
              {t("facilityManagement.actions.manageFacility")}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem id="delete" textValue={t("other.deleteButton")} variant="destructive">
              <Trash2 />
              {t("other.deleteButton")}
            </DropdownMenuItem>
          </Menu>
        </Dialog>
      </Popover>
    </PopoverTrigger>
  );
}
